import { useCallback } from 'react';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useWorkflowViewPrefsStore } from '@/stores/workflowViewPrefsStore';

/**
 * Returns `true` when the workflow canvas is rendering horizontally
 * (left-to-right flow). Subscribes to `layoutOrientation` only, so consumers
 * re-render only when the orientation actually flips.
 */
export const useIsHorizontalLayout = (): boolean =>
  useWorkflowStore((state) => state.layoutOrientation === 'horizontal');

/**
 * Returns a stable callback that flips the workflow canvas between
 * `horizontal` and `vertical` layouts. Centralizes the toggle so callers
 * (toolbar button, command palette, etc.) don't reimplement the ternary.
 *
 * Auto-formatting after toggle is handled by an effect inside
 * `WorkflowDesigner` so it can reuse the existing layout pipeline.
 */
export const useToggleLayoutOrientation = (): (() => void) => {
  const setLayoutOrientation = useWorkflowStore((state) => state.setLayoutOrientation);
  return useCallback(() => {
    const current = useWorkflowStore.getState().layoutOrientation;
    setLayoutOrientation(current === 'horizontal' ? 'vertical' : 'horizontal');
  }, [setLayoutOrientation]);
};

/**
 * Persisted "canvas locked" preference. When true, the canvas blocks node
 * dragging, edge creation, and element selection. Backed by
 * `workflowViewPrefsStore` so the choice survives reloads.
 */
export const useIsCanvasLocked = (): boolean =>
  useWorkflowViewPrefsStore((state) => state.defaults.canvasLocked);

export const useToggleCanvasLock = (): (() => void) => {
  const setCanvasLocked = useWorkflowViewPrefsStore((state) => state.setCanvasLocked);
  return useCallback(() => {
    setCanvasLocked(!useWorkflowViewPrefsStore.getState().defaults.canvasLocked);
  }, [setCanvasLocked]);
};
